import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  Tooltip
} from '@mui/material';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { tArray, tObject } from '../lib/i18nSafe';

// ─────────────────────────────────────────────────────────────
// StreamingDemo
// ─────────────────────────────────────────────────────────────
// A client-side, zero-dependency recreation of the "embedded live
// playground" pattern that dominates 2026 AI company homepages.
//
// It simulates token-by-token streaming for an illustrative,
// Arabic-first assistant demo (canned responses; not a real or
// proprietary model), complete with:
//   • Example prompt chips (click-to-try)
//   • Editable prompt box
//   • Streaming response with a blinking caret
//   • Stop / Regenerate / Copy controls
//   • A mono "latency" readout, to evoke a real dev console
//
// All content is sourced from the i18n translation files so it
// localises cleanly into en / fr / ar (including RTL).

const STREAM_INTERVAL_MS = 24; // per-token delay

function tokeniseForStream(text) {
  // Keep punctuation attached; split on spaces while preserving them.
  return text.match(/\S+\s?|\s+/g) || [];
}

export default function StreamingDemo({ compact = false }) {
  const { t } = useTranslation();

  const examples = useMemo(() => tArray(t, 'playgroundDemo.examples'), [t]);
  const answers = useMemo(() => tObject(t, 'playgroundDemo.answers'), [t]);

  const [prompt, setPrompt] = useState(examples?.[0]?.prompt || '');
  const [output, setOutput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [latencyMs, setLatencyMs] = useState(null);
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);
  const startRef = useRef(0);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const stop = () => {
    clearInterval(timerRef.current);
    timerRef.current = null;
    setIsStreaming(false);
  };

  const run = (text) => {
    const p = (text ?? prompt).trim();
    if (!p) return;
    stop();
    setPrompt(p);
    setOutput('');
    setLatencyMs(null);

    const match =
      examples.find((e) => e.prompt === p) || {
        id: 'generic',
        prompt: p
      };
    const reply = answers[match.id] || answers.generic || '';
    const tokens = tokeniseForStream(reply);

    setIsStreaming(true);
    startRef.current = performance.now();

    let i = 0;
    timerRef.current = setInterval(() => {
      i += 1;
      setOutput(tokens.slice(0, i).join(''));
      if (i >= tokens.length) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        setIsStreaming(false);
        setLatencyMs(Math.round(performance.now() - startRef.current));
      }
    }, STREAM_INTERVAL_MS);
  };

  const copy = async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch (_) {
      /* ignore */
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        borderColor: 'divider',
        boxShadow: '0 1px 0 rgba(20,20,19,0.02), 0 20px 60px -30px rgba(20,20,19,0.18)'
      }}
    >
      {/* Toolbar */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: 2,
          py: 1.2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.default'
        }}
      >
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: isStreaming ? 'primary.main' : 'text.secondary',
            opacity: isStreaming ? 1 : 0.5,
            animation: isStreaming ? 'alamen-pulse 1.1s ease-in-out infinite' : 'none',
            '@keyframes alamen-pulse': {
              '0%,100%': { opacity: 0.45 },
              '50%': { opacity: 1 }
            }
          }}
        />
        <Typography
          sx={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.72rem',
            letterSpacing: '0.06em',
            color: 'text.secondary',
            textTransform: 'uppercase'
          }}
        >
          {t('playgroundDemo.modelLabel')} · {t('playgroundDemo.demoBadge')}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {latencyMs != null ? (
          <Typography
            sx={{
              fontFamily: 'var(--font-mono)',
              fontSize: '0.72rem',
              color: 'text.secondary'
            }}
          >
            {latencyMs} ms
          </Typography>
        ) : null}
        <Tooltip title={t('playgroundDemo.regenerate')}>
          <span>
            <IconButton size="small" onClick={() => run()} disabled={!prompt}>
              <ReplayRoundedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={copied ? t('playgroundDemo.copied') : t('playgroundDemo.copy')}>
          <span>
            <IconButton size="small" onClick={copy} disabled={!output}>
              {copied ? <CheckRoundedIcon fontSize="small" /> : <ContentCopyRoundedIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {/* Prompt area */}
      <Box sx={{ p: { xs: 2, md: 2.5 } }}>
        <Stack direction="row" spacing={1} sx={{ mb: 1.25, flexWrap: 'wrap', gap: 1 }}>
          {examples.slice(0, compact ? 2 : 3).map((ex) => (
            <Chip
              key={ex.id}
              label={ex.label}
              size="small"
              onClick={() => run(ex.prompt)}
              sx={{
                bgcolor: 'transparent',
                border: '1px solid',
                borderColor: 'divider',
                color: 'text.secondary',
                cursor: 'pointer',
                fontFamily: 'var(--font-mono)',
                '&:hover': {
                  borderColor: 'primary.main',
                  color: 'primary.main'
                }
              }}
            />
          ))}
        </Stack>

        <TextField
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              run();
            }
          }}
          fullWidth
          multiline
          minRows={compact ? 2 : 3}
          maxRows={6}
          placeholder={t('playgroundDemo.placeholder')}
          InputProps={{
            sx: {
              fontFamily: 'var(--font-mono)',
              fontSize: '0.9rem',
              bgcolor: 'background.default',
              borderRadius: 2
            }
          }}
        />

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
          <Typography
            sx={{
              fontFamily: 'var(--font-mono)',
              fontSize: '0.72rem',
              color: 'text.secondary'
            }}
          >
            {t('playgroundDemo.hint')}
          </Typography>
          <Box sx={{ flex: 1 }} />
          {isStreaming ? (
            <Button variant="outlined" size="small" onClick={stop}>
              {t('playgroundDemo.stop')}
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              onClick={() => run()}
              disabled={!prompt.trim()}
              endIcon={<SendRoundedIcon fontSize="small" />}
              sx={{
                bgcolor: 'text.primary',
                color: 'background.default',
                '&:hover': { bgcolor: 'text.primary', opacity: 0.9 }
              }}
            >
              {t('playgroundDemo.run')}
            </Button>
          )}
        </Stack>
      </Box>

      {/* Output area */}
      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.default',
          px: { xs: 2, md: 2.5 },
          py: 2,
          minHeight: compact ? 110 : 160
        }}
      >
        <Typography
          sx={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.7rem',
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            mb: 1
          }}
        >
          {t('playgroundDemo.response')}
        </Typography>
        <Typography
          sx={{
            whiteSpace: 'pre-wrap',
            fontFamily: 'var(--font-sans)',
            fontSize: '0.95rem',
            lineHeight: 1.6,
            color: 'text.primary',
            minHeight: '1.6em'
          }}
        >
          {output || (
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {t('playgroundDemo.emptyState')}
            </Box>
          )}
          {isStreaming ? <Box component="span" className="alamen-caret" sx={{ ml: 0.25 }} /> : null}
        </Typography>
      </Box>
    </Paper>
  );
}
